<template>
    <div>
        <!-- Modals -->
        <ImageExtensionModal
            v-model="showImageModal"
            :type="'image'"
            @save="insertImage"
            @hide="showImageModal = false"
        ></ImageExtensionModal>
        <EmbedExtensionModal
            v-model="showEmbedModal"
            :type="'embed'"
            @save="insertEmbed"
            @hide="showEmbedModal = false"
        ></EmbedExtensionModal>
        <LinkExtensionModal
            v-model="showLinkModal"
            @save="insertLink"
            @hide="showLinkModal = false"
        />

        <!-- Editor Menu -->
        <EditorMenuBar
            :editor="editor"
            v-slot="{ commands, isActive, getMarkAttrs, focused }"
        >
            <div class="menubar mb-2">
                <!-- HTML / Text Btns -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-button
                        :variant="editingHtml ? 'success' : ''"
                        v-b-popover.hover.top="'Edit HTML'"
                        @click="editHtml"
                    >
                        <font-awesome-icon :icon="['far', 'code']" />
                    </b-button>

                </b-button-group>

                <!-- Bold / Italic Marks -->
                <b-button-group :size="btnSize">
                    <b-button @click="commands.bold" :variant="isActive.bold() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'bold']" />
                    </b-button>

                    <b-button @click="commands.italic" :variant="isActive.italic() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'italic']" />
                    </b-button>

                    <b-button @click="commands.strike" :variant="isActive.strike() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'strikethrough']" />
                    </b-button>

                    <b-button @click="commands.underline" :variant="isActive.underline() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'underline']" />
                    </b-button>

                    <b-popover custom-class="p-0 font-popover" :target="`${_uid}_font_size_btn`" triggers="click" placement="top">
                        <FontSizePicker @save="commands.fontSize({fontSize: `${arguments[0]}px`})"/>
                    </b-popover>
                    <b-button :id="`${_uid}_font_size_btn`">
                        <font-awesome-icon :icon="['fas', 'text-size']" />
                    </b-button>

                    <!--                    <b-dropdown>-->
                    <!---->
                    <!--                    </b-dropdown>-->
                </b-button-group>

                <!-- Font Marks -->
                <b-button-group :size="btnSize">
                    <b-popover custom-class="p-0 font-popover" :target="`${_uid}_font_color_btn`" triggers="click" placement="top">
                        <ColorPicker @save="commands.color({color: arguments[0]})" />
                    </b-popover>
                    <b-btn :id="`${_uid}_font_color_btn`"
                           :variant="isActive.color() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'font']" />
                    </b-btn>
                    <b-popover custom-class="p-0 font-popover" :target="`${_uid}_font_highlight_btn`" triggers="click" placement="top">
                        <ColorPicker @save="commands.highlight({backgroundColor: arguments[0]})" />
                    </b-popover>
                    <b-btn :id="`${_uid}_font_highlight_btn`">
                        <font-awesome-icon :icon="['fas', 'highlighter']" />
                    </b-btn>
                </b-button-group>

                <!-- Align Marks -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-btn :variant="isActive.paragraph({textAlign: 'left'}) ? 'success' :''"
                           @click="commands.paragraph({ textAlign: 'left' })">
                        <font-awesome-icon :icon="['fas', 'align-left']" />
                    </b-btn>
                    <b-btn :variant="isActive.paragraph({textAlign: 'center'}) ? 'success' :''"
                           @click="commands.paragraph({ textAlign: 'center' })">
                        <font-awesome-icon :icon="['fas', 'align-center']" />
                    </b-btn>
                    <b-btn :variant="isActive.paragraph({textAlign: 'right'}) ? 'success' :''"
                           @click="commands.paragraph({ textAlign: 'right' })">
                        <font-awesome-icon :icon="['fas', 'align-right']" />
                    </b-btn>
                </b-button-group>

                <!-- Node Types (List/Bullet) -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-button @click="commands.bullet_list" :variant="isActive.bullet_list() ? 'success' :''">
                        <font-awesome-icon :icon="['far', 'list-ul']" />
                    </b-button>
                    <b-button @click="commands.ordered_list" :variant="isActive.ordered_list() ? 'success' :''">
                        <font-awesome-icon :icon="['far', 'list-ol']" />
                    </b-button>
                </b-button-group>

                <!-- Image / Link Nodes -->
                <b-button-group v-if="!simple" :size="btnSize">
                    <b-button @click="addLink(commands.link, getMarkAttrs)" :variant="isActive.link() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'link']" />
                    </b-button>
                    <b-button @click="commands.link({href: null})" v-show="isActive.link() ? 'success' :''">
                        <font-awesome-icon :icon="['fas', 'unlink']" />
                    </b-button>
                    <b-button @click="addImage(commands.image)">
                        <font-awesome-icon :icon="['far', 'image']" />
                    </b-button>
                    <b-button @click="addEmbed(commands.embed)">
                        IFrame
                        <font-awesome-icon :icon="['fat', 'code-simple']" />
                    </b-button>
                </b-button-group>
            </div>
        </EditorMenuBar>
    </div>
</template>

<script>
import EmbedExtension from "@/components/tiptap/extensions/Nodes/embed_extension.js";
import EmbedExtensionModal from "@/components/tiptap/extensions/EmbedExtensionModal";

import ImageExtensionModal from "@/components/tiptap/extensions/ImageExtensionModal";
import LinkExtensionModal from "@/components/tiptap/extensions/LinkExtensionModal";
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble, Text } from 'tiptap'
import FontSizePicker from "@/components/tiptap/extensions/FontSizePicker";
import ColorPicker from "@/components/tiptap/extensions/ColorPicker";

export default {
    name: "TipTapMenu",
    components: {ColorPicker, FontSizePicker, LinkExtensionModal, ImageExtensionModal, EmbedExtensionModal, EditorMenuBar},
    props: ["editor", "editingHtml"],
    data: () => ({
        simple: false,
        btnSize: 'sm',
        showImageModal: false,
        showEmbedModal: false,
        showLinkModal: false,
        currentLink: {
            url: null,
        },
        imageSettings: {
            command: null,
            src: null,
            width: null,
            height: null,
        },
        embedSettings: {
            command: null,
            src: null,
            width: null,
            height: null,
        },
        linkSettings: {
            command: null,
            target: '_blank',
            getMarkAttrs: null,
        }
    }),
    methods: {
        addImage(command) {
            this.imageSettings.command = command;
            // this.$bvModal.show('image_modal');
            this.showImageModal = true;
        },
        insertImage({src, width, align, alt, caption, credit}) {
            if (width)
                width = `${width}%`;

            this.editor.commands.imageNode({
                align: align,
                alt: alt ? alt: null,
                caption: caption ? caption: null,
                credit: credit ? credit: null,
                src: src,
                width: width ? width: null,
            })

            this.$emit('update');
            this.showImageModal = false;
        },
        addLink(command, getMarkAttrs) {
            this.linkSettings.command = command;
            this.linkSettings.getMarkAttrs = getMarkAttrs;
            this.showLinkModal = true;
        },
        insertLink({url}) {
            this.linkSettings.command({href: url});
            this.$emit('update');
            this.showLinkModal = false;
        },
        editHtml() {
            this.$emit('editHtml')
        },
        addEmbed(command) {
            this.embedSettings.command = command;
            // this.$bvModal.show('image_modal');
            this.showEmbedModal = true;
        },
        insertEmbed({src, width, height, align, alt, caption, credit}) {
            if (width)
                width = `${width}%`;

            this.editor.commands.embedNode({
                align: align,
                alt: alt ? alt: null,
                caption: caption ? caption: null,
                credit: credit ? credit: null,
                src: src,
                width: width ? width: null,
                height: height ? height: null,

            })

            this.$emit('update');
            this.showEmbedModal = false;
        },
    }
}
</script>

<style scoped>
.btn-group {
    margin-right: 5px;
}
</style>